/**
 * Typed accessor for runtime config injected at container start (`nginx/entrypoint.sh`) — one
 * image is shared across environments via container env vars, not `VITE_*` build-time vars.
 * Missing Entra values fail fast here rather than crashing deep inside the auth flow.
 */
interface RuntimeConfig {
  readonly apiBaseUrl: string;
  readonly appInsightsConnectionString: string | null;
  /** Entra tenant id (GUID), e.g. `d3cbc782-f9fe-47ce-af2b-e80c34db6405`. */
  readonly entraTenantId: string;
  /** Entra Native Auth frontend app-registration (client) id. */
  readonly entraClientId: string;
  /**
   * CIAM subdomain of the Entra authority, e.g. `delabo` for `https://delabo.ciamlogin.com` —
   * combined with `entraTenantId` in `entraNativeAuthClient.ts` to build the authority URL.
   */
  readonly entraAuthoritySubdomain: string;
  /**
   * Optional override for the full Entra authority base URL, used verbatim instead of the
   * derived `https://<subdomain>.ciamlogin.com/<tenantId>` URL. LOCAL DEV ONLY: the Entra
   * Native Auth endpoints don't return a CORS-compliant response for a cross-origin browser
   * call, so point this at the standalone `scripts/entra-cors-proxy.mjs` proxy (run via
   * `npm run dev:entra-proxy` in a second terminal), e.g. `http://localhost:3001/<tenantId>`;
   * leave unset in cloud profiles.
   */
  readonly entraAuthorityBaseUrl: string | null;
}

function requireString(raw: string | undefined, key: string): string {
  if (raw === undefined || raw.trim() === '') {
    throw new Error(
      `Runtime config error: window.__DELABO_RUNTIME_CONFIG__.${key} is missing. ` +
        `Check the container env vars (see nginx/entrypoint.sh) or public/env.js for local dev.`,
    );
  }
  return raw;
}

function getRuntimeConfig(): RuntimeConfig {
  const raw = window.__DELABO_RUNTIME_CONFIG__ ?? {};
  return {
    apiBaseUrl: raw.apiBaseUrl ?? '/api/v1',
    appInsightsConnectionString:
      raw.appInsightsConnectionString && raw.appInsightsConnectionString.trim() !== ''
        ? raw.appInsightsConnectionString
        : null,
    entraTenantId: requireString(raw.entraTenantId, 'entraTenantId'),
    entraClientId: requireString(raw.entraClientId, 'entraClientId'),
    entraAuthoritySubdomain: requireString(raw.entraAuthoritySubdomain, 'entraAuthoritySubdomain'),
    entraAuthorityBaseUrl:
      raw.entraAuthorityBaseUrl && raw.entraAuthorityBaseUrl.trim() !== ''
        ? raw.entraAuthorityBaseUrl
        : null,
  };
}

export const runtimeConfig: RuntimeConfig = getRuntimeConfig();
